/**
 * AddToPlaylistSheet — "+ Playlist": pick one of my built playlists, or create one inline
 * and add the track to it. Bottom sheet on mobile, popover on desktop.
 */

import React, { useEffect, useState } from 'react';
import { addTrack, createPlaylist, myPlaylists } from '../../lib/archiveApi';
import { track as trackEvent } from '../../lib/analytics';

const MAX_TRACKS = 50;

export default function AddToPlaylistSheet({ track, identifier, itemSource, artistTag, onClose, onAdded }) {
  const added = () => trackEvent('playlist_track_added', { source: itemSource, tag: artistTag });
  const [lists, setLists] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    let alive = true;
    myPlaylists()
      .then((d) => { if (alive) setLists(d.playlists || []); })
      .catch((e) => { if (alive) { setLists([]); setError(e.message); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const add = async (playlist) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await addTrack(playlist.id, identifier, track.fileName);
      added();
      onAdded(playlist);
    } catch (e) {
      setError(e.code === 'DUPLICATE_TRACK' ? `Already in “${playlist.title}”.` : e.message);
    } finally {
      setBusy(false);
    }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    const title = name.trim();
    if (!title || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { playlist } = await createPlaylist({ title });
      trackEvent('playlist_built');
      await addTrack(playlist.id, identifier, track.fileName);
      added();
      onAdded(playlist);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ax-scrim" onClick={onClose} role="presentation" />
      <div className="ax-sheet" role="dialog" aria-modal="true" aria-label="Add to playlist">
        <div className="ax-row">
          <div className="ax-grow">
            <div className="ax-title">Add “{track.title}” to…</div>
            <div className="ax-meta">Tap a playlist</div>
          </div>
          <button type="button" className="ax-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {error && <div className="ax-banner ax-banner--error">{error}</div>}
        {lists === null && <div className="ax-skel" style={{ height: 48, marginTop: 12 }} />}
        {(lists || []).map((p) => {
          const full = p.trackCount >= MAX_TRACKS;
          const left = MAX_TRACKS - p.trackCount;
          return (
            <button type="button" key={p.id} className="ax-opt" disabled={full || busy} onClick={() => add(p)}>
              <div className="ax-grow">
                <div className="ax-title">{p.title}</div>
                <div className="ax-meta">
                  {full ? `${MAX_TRACKS} / ${MAX_TRACKS} · full` : `${p.trackCount} track${p.trackCount === 1 ? '' : 's'} · ${p.isPublic ? 'public' : 'private'}${left <= 5 ? ` · ${left} slot${left === 1 ? '' : 's'} left` : ''}`}
                </div>
              </div>
              {!full && <span aria-hidden="true">›</span>}
            </button>
          );
        })}
        {lists && lists.length === 0 && !error && <div className="ax-meta" style={{ margin: '10px 0' }}>No playlists yet — name your first one.</div>}
        <form className="ax-search" style={{ marginTop: 12 }} onSubmit={createAndAdd}>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="New playlist name…" aria-label="New playlist name" />
          <button type="submit" className="ax-btn ax-btn--sm ax-btn--primary" disabled={!name.trim() || busy}>Create &amp; add</button>
        </form>
      </div>
    </>
  );
}
